import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { FreeFilterInputComponent } from '../free-filter-input/free-filter-input.component';
import { CompetencyFilterCBComponent } from '../competency-filter-combobox/competency-cb.component';
import { CollectionFilter } from '../../../core/jsonApi/BaseCollectionService';
import { StateFilterCBComponent } from '../state-filter-combobox/state-cb.component';
import { AssetService } from '../../../asset/services/asset.service';
import { Store } from '@ngxs/store';
import { FetchOptionsState } from '../../../core/state/fetchOptions.state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConsumerJsonModel } from '../../../consumer/consumer.json.model';
import { SubjectJsonModel } from '../../../subject/subject.json.model';
import { AssetJsonModel } from '../../../asset/assets.json.model';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MainPaginationComponent } from '../../../core/components/main-pagination/main-pagination.component';
import { ChooseMedium } from './choose-medium.json.model';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Md5 } from 'ts-md5';
import { environment } from '../../../../environments/environment';
import { getMinFilterLengthText } from '../../../core/util';

@Component({
  selector: 'pixl-choose-medium',
  imports: [
    MatDividerModule,
    MatDialogContent,
    MatDialogTitle,
    MatDialogActions,
    MatButtonModule,
    MatSortModule,
    MatTableModule,
    MatIcon,
    MatTooltipModule,
    FreeFilterInputComponent,
    CompetencyFilterCBComponent,
    StateFilterCBComponent,
    MainPaginationComponent
  ],
  templateUrl: './choose-medium.component.html',
  styleUrl: './choose-medium.component.scss'
})
export class ChooseMediumComponent implements OnInit {
  @ViewChild(MatSort) sort!: MatSort;

  private readonly dialogRef = inject(MatDialogRef<ChooseMediumComponent>);
  private readonly store = inject(Store);
  private freeFilter: string | undefined;
  private competencyFilter: string | undefined;
  private stateFilter: string | undefined;
  private sortFieldName: string | null = null;
  private sortFieldDirection: string | null = null;
  private _liveAnnouncer = inject(LiveAnnouncer);
  private assetService = inject(AssetService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private displayedColumns: string[] = ['action', 'name', 'tags'];

  public dataSource = new MatTableDataSource<AssetJsonModel>([] as AssetJsonModel[]);
  public columnsToDisplay: string[] = this.displayedColumns.slice();
  public filters: CollectionFilter[] = [];
  public data = inject<ChooseMedium>(MAT_DIALOG_DATA);
  public consumers: ConsumerJsonModel[] = []; // List of all available consumers
  public competencies: SubjectJsonModel[] = []; // List of all available competencies

  public readonly freeFilterLen = 3; // Min length of free filter input

  constructor() {
    if (this.data.mediaType === 'Image') {
      this.columnsToDisplay.push('image');
    }

    if (this.data.mediaType === 'Audio') {
      this.columnsToDisplay.push('audio');
    }
  }

  private getBaseUrl(): string {
    if (environment.isProd) {
      return '/';
    }
    return 'https://zav-stage.westermann.de/';
  }

  private createMD5Path(fileName: string): string {
    let path = '';

    Md5
      .hashStr(fileName)
      .split(/(.{8})/)
      .filter(O => O)
      .forEach((entry, idx) => {
        path += ((idx > 0) ? '/' : '') + entry.toUpperCase();
      });

    return path;
  }

  public getImageUrl(asset: AssetJsonModel): string {
    let imageUrl = this.getBaseUrl();

    if (asset.mediaType === 'Image') {
      imageUrl += 'media/image/';
    }

    if (asset.mediaType === 'Animation') {
      imageUrl += 'media/animation/';
    }

    imageUrl += this.createMD5Path(asset.name);

    if (asset.mediaType === 'Image') {
      imageUrl += '.png';
    }

    if (asset.mediaType === 'Animation') {
      imageUrl += '.gif';
    }

    return imageUrl;
  }

  public getAudioUrl(asset: AssetJsonModel): string {
    let audioUrl = this.getBaseUrl() + 'media/audio/';

    audioUrl += this.createMD5Path(asset.name) + '.mp3';

    return audioUrl;
  }

  public ngOnInit(): void {
    this.dataSource.sort = this.sort;
    this.setDefaultSort();

    this.setFilters();
  }

  private setDefaultSort() {
    this.sortFieldName = 'name';
    this.sortFieldDirection = 'asc';
  }

  private setFilters(): void {
    this.filters = [];

    // Media type filter set as default
    this.filters.push({ fieldName: 'mediaType', value: this.data.mediaType !== undefined ? this.data.mediaType.toString() : '' });

    this.filters.push({ fieldName: 'active', value: true });

    if (this.competencyFilter) {
      this.filters.push({ fieldName: 'treeId', value: this.competencyFilter });
    }

    if (this.freeFilter) {
      this.filters.push({ fieldName: 'nameLike', value: this.freeFilter });
    }

    if (this.stateFilter) {
      this.filters.push({ fieldName: 'approval', value: this.stateFilter });
    }

    this.getListData();
  }

  private sortData(sort: Sort): void {
    const data = this.dataSource.data.slice();

    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
    }
    else {
      this.dataSource.data = data.sort((a: AssetJsonModel, b: AssetJsonModel) => {
        const aValue = a[sort.active as keyof AssetJsonModel];
        const bValue = b[sort.active as keyof AssetJsonModel];
        if (aValue && bValue) {
          return (aValue < bValue ? -1 : 1) * (sort.direction === 'asc' ? 1 : -1);
        }
        else {
          return 0;
        }
      });
    }
  }

  public getSearchFilterHint(): string {
    return getMinFilterLengthText(this.freeFilterLen);
  }

  public setFreeFilter(ev: string) {
    this.freeFilter = ev;
    this.setFilters();
  }

  public setCompetencyFilter(ev: string): void {
    this.competencyFilter = ev;
    this.setFilters();
  }

  public setStateFilter(ev: string): void {
    this.stateFilter = ev;
    this.setFilters();
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  public paginationChanged = (): void => {
    this.getListData();
  };

  public getTags(asset: AssetJsonModel): string {
    let retValue = '';
    if (asset.tags && asset.tags.length > 0) {
      asset.tags.forEach((entry, idx) => {
        retValue += (idx > 0 ? ' ' : '') + `<button type="button" class="tags">${entry}</button>`;
      });
    }
    return retValue;
  }

  public getInitOption(): string {
    return 'Live';
  }

  public getListData(): void {
    this.assetService.getAllWithPagination({}, {
      pageNumber: this.store.selectSnapshot(FetchOptionsState.getPageNumber),
      pageSize: this.store.selectSnapshot(FetchOptionsState.getPageSize),
      filters: this.filters,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          let [data] = response;
          data = [...data.sort((a, b) => {
            return a.name.localeCompare(b.name);
          })];
          this.dataSource = new MatTableDataSource<AssetJsonModel>(data);
          this.cdr.markForCheck();
        },
        error: () => {
          // is handled in requestInterceptor.ts
        },
      });
  }

  public takeAsset(idx: number): void {
    this.dialogRef.close(this.dataSource.data[idx]);
  }

  public onCancel(): void {
    this.dialogRef.close();
  }
}
